import React, { useEffect, useMemo, useState } from 'react';
import { DEFAULT_PARTNER_FORM_OPTIONS } from '../../Constants/partnerFormOptions';
import { PARTNER_FIELD_LABELS } from '../../Utils/partnerApiFeedback';
import { promptForValidProjectLeadEmail } from '../../Utils/partnerEmailPrompt';
import { formHasTypedContent } from '../../Utils/partnerSubmitDraftUtils';
import { isValidProjectLeadEmail, validatePartnerProjectPayload } from '../../Utils/partnerValidation';
import PartnerDateInput from './PartnerDateInput';
import { getSubmitProjectStatusOptions } from '../../Utils/partnerProjectStatusUtils';

const getInitialForm = (prefill = {}, draftForm = null) => ({
  partnerName: draftForm?.partnerName || prefill.partnerName || '',
  csn: draftForm?.csn || prefill.csn || '',
  customerName: draftForm?.customerName || '',
  projectLeadName: draftForm?.projectLeadName || '',
  projectLeadEmail: draftForm?.projectLeadEmail || '',
  productsInScope: Array.isArray(draftForm?.productsInScope) ? draftForm.productsInScope : [],
  engagementType: draftForm?.engagementType || '',
  deploymentModel: draftForm?.deploymentModel || '',
  projectStatus: draftForm?.projectStatus || '',
  estimatedStartDate: draftForm?.estimatedStartDate || '',
  estimatedGoliveDate: draftForm?.estimatedGoliveDate || '',
  projectDescription: draftForm?.projectDescription || '',
  salesforceOpportunityId: draftForm?.salesforceOpportunityId || '',
  competitiveContext: draftForm?.competitiveContext || '',
  additionalNotes: draftForm?.additionalNotes || '',
});

const controlClass = (baseClass, hasError) => {
  const dropdownClass = baseClass === 'form-select' ? 'partner-form-select ' : '';
  return `${dropdownClass}${baseClass}${hasError ? ' is-invalid' : ''}`;
};

const getValidationPayload = (formState, hidePartnerFields) => (
  hidePartnerFields
    ? { ...formState, partnerName: 'selected', csn: 'selected' }
    : formState
);

const fieldHasValue = (form, field) => {
  const value = form[field];
  if (field === 'productsInScope') {
    return Array.isArray(value) && value.length > 0;
  }
  return value !== null && value !== undefined && String(value).trim() !== '';
};

const FormSection = ({
  step,
  title,
  subtitle,
  optional = false,
  children,
}) => (
  <section className="partner-form-section">
    <header className="partner-form-section-head">
      <span className="partner-form-step" aria-hidden="true">{step}</span>
      <div className="partner-form-section-copy">
        <h3 className="partner-form-section-title">
          {title}
          {optional ? <span className="partner-form-section-optional">Optional</span> : null}
        </h3>
        {subtitle ? <p className="partner-form-section-subtitle">{subtitle}</p> : null}
      </div>
    </header>
    <div className="partner-form-section-body">
      {children}
    </div>
  </section>
);

const FormField = ({
  label,
  required = false,
  hint,
  error,
  className = '',
  children,
}) => (
  <div className={`partner-field ${className} ${error ? 'has-error' : ''}`.trim()}>
    <label className="partner-field-label">
      {label}
      {required ? <span className="partner-field-required" aria-hidden="true">*</span> : null}
    </label>
    {children}
    {error ? <small className="text-danger">{error}</small> : null}
    {hint && !error ? <small className="partner-helper-text">{hint}</small> : null}
  </div>
);

const ProjectForm = ({
  prefill = {},
  draftForm = null,
  formOptions = DEFAULT_PARTNER_FORM_OPTIONS,
  onSubmit,
  onFieldEdit,
  onFormChange,
  submitting = false,
  submitError = '',
  serverErrors = {},
  hidePartnerFields = false,
  submitButtonLabel = 'Submit project',
}) => {
  const [form, setForm] = useState(() => getInitialForm(prefill, draftForm));
  const [errors, setErrors] = useState({});
  const projectStatusOptions = useMemo(
    () => getSubmitProjectStatusOptions(formOptions.projectStatus),
    [formOptions.projectStatus],
  );
  const isPrefilledPartnerName = Boolean(prefill.partnerName);
  const isPrefilledCsn = Boolean(prefill.csn);

  useEffect(() => {
    setForm((previous) => ({
      ...previous,
      partnerName: prefill.partnerName || previous.partnerName,
      csn: prefill.csn || previous.csn,
    }));
  }, [prefill.partnerName, prefill.csn]);

  useEffect(() => {
    const excludeFields = [
      ...(isPrefilledPartnerName ? ['partnerName'] : []),
      ...(isPrefilledCsn ? ['csn'] : []),
    ];
    onFormChange?.(form, formHasTypedContent(form, { excludeFields }));
  }, [form, onFormChange, isPrefilledPartnerName, isPrefilledCsn]);

  const validation = useMemo(() => (
    validatePartnerProjectPayload(getValidationPayload(form, hidePartnerFields))
  ), [form, hidePartnerFields]);

  const displayErrors = useMemo(() => {
    const merged = { ...serverErrors, ...errors };
    if (hidePartnerFields) {
      delete merged.partnerName;
      delete merged.csn;
      delete merged.partnerEmail;
      delete merged.companyName;
    }
    return merged;
  }, [serverErrors, errors, hidePartnerFields]);

  const progressTrackFields = useMemo(() => {
    const fields = [
      'customerName',
      'projectLeadName',
      'projectLeadEmail',
      'engagementType',
      'deploymentModel',
      'projectStatus',
      'estimatedStartDate',
      'estimatedGoliveDate',
      'projectDescription',
    ];
    if (!hidePartnerFields) {
      return ['partnerName', 'csn', ...fields];
    }
    return fields;
  }, [hidePartnerFields]);

  const progressPercent = useMemo(() => {
    const totalSteps = progressTrackFields.length + 1;
    let completed = progressTrackFields.filter((field) => fieldHasValue(form, field)).length;
    if (fieldHasValue(form, 'productsInScope')) {
      completed += 1;
    }
    return Math.min(100, Math.round((completed / totalSteps) * 100));
  }, [form, progressTrackFields]);

  const progressSections = useMemo(() => {
    const countSection = (fields) => {
      const total = fields.length;
      const done = fields.filter((field) => fieldHasValue(form, field)).length;
      return {
        done,
        total,
        percent: total ? Math.round((done / total) * 100) : 0,
        complete: total > 0 && done === total,
      };
    };

    const sectionOneFields = hidePartnerFields
      ? ['customerName', 'projectLeadName', 'projectLeadEmail']
      : ['partnerName', 'csn', 'customerName', 'projectLeadName', 'projectLeadEmail'];
    const sectionTwoFields = [
      'engagementType',
      'deploymentModel',
      'projectStatus',
      'estimatedStartDate',
      'estimatedGoliveDate',
      'productsInScope',
      'projectDescription',
    ];

    return [
      {
        key: 'partner',
        label: hidePartnerFields ? 'Customer' : 'Partner',
        ...countSection(sectionOneFields),
      },
      {
        key: 'details',
        label: 'Details',
        ...countSection(sectionTwoFields),
      },
      {
        key: 'optional',
        label: 'Optional',
        optional: true,
        ...countSection(['competitiveContext', 'additionalNotes']),
      },
    ];
  }, [form, hidePartnerFields]);

  const progressHeadline = useMemo(() => {
    if (validation.isValid) {
      return 'Ready to submit';
    }
    if (progressPercent >= 85) {
      return 'Almost there';
    }
    if (progressPercent >= 45) {
      return 'Good progress';
    }
    if (progressPercent > 0) {
      return 'In progress';
    }
    return 'Start with section 1';
  }, [progressPercent, validation.isValid]);

  const completedSectionCount = progressSections.filter((section) => section.complete).length;

  const statusMessage = useMemo(() => {
    if (submitError) {
      return submitError;
    }

    const errorEntries = Object.entries(displayErrors);
    if (errorEntries.length > 0) {
      const [field, message] = errorEntries[0];
      const label = PARTNER_FIELD_LABELS[field] || field;
      return `${label}: ${message}`;
    }

    return validation.isValid ? 'Ready to submit — review and send to your PAE' : 'Complete required fields to submit';
  }, [submitError, displayErrors, validation.isValid]);

  const statusTone = useMemo(() => {
    if (submitError || Object.keys(displayErrors).length > 0) {
      return 'error';
    }
    if (validation.isValid) {
      return 'success';
    }
    return 'progress';
  }, [submitError, displayErrors, validation.isValid]);

  useEffect(() => {
    if (Object.keys(serverErrors).length > 0) {
      const firstInvalid = document.querySelector('.partner-form--modern .is-invalid, .partner-products-grid.has-error');
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [serverErrors]);

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const nextErrors = { ...previous };
      delete nextErrors[field];
      return nextErrors;
    });
    onFieldEdit?.(field);
  };

  const onToggleProduct = (product) => {
    const selected = new Set(form.productsInScope);
    if (selected.has(product)) {
      selected.delete(product);
    } else {
      selected.add(product);
    }
    updateField('productsInScope', Array.from(selected));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let workingForm = { ...form };

    if (!isValidProjectLeadEmail(workingForm.projectLeadEmail)) {
      const correctedEmail = await promptForValidProjectLeadEmail(workingForm.projectLeadEmail);
      if (!correctedEmail) {
        setErrors({
          projectLeadEmail: 'Enter a valid email with @ and . (e.g. name@company.com)',
        });
        return;
      }
      workingForm = { ...workingForm, projectLeadEmail: correctedEmail };
      setForm(workingForm);
    }

    const check = validatePartnerProjectPayload(
      getValidationPayload(workingForm, hidePartnerFields),
    );
    if (!check.isValid) {
      setErrors(check.errors);
      return;
    }
    setErrors({});
    onSubmit(workingForm);
  };

  const selectedProductCount = form.productsInScope.length;

  return (
    <form className="partner-form partner-form--modern" onSubmit={handleSubmit}>
      <div
        className={`partner-form-progress-panel ${validation.isValid ? 'is-complete' : ''}`}
      >
        <div className="partner-form-progress-main">
          <div
            className="partner-form-progress-ring"
            style={{ '--progress-percent': progressPercent }}
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Form ${progressPercent} percent complete`}
          >
            <span className="partner-form-progress-ring-value">{progressPercent}%</span>
          </div>
          <div className="partner-form-progress-meta">
            <strong className="partner-form-progress-headline">{progressHeadline}</strong>
            <p className="partner-form-progress-subline">
              {completedSectionCount} of {progressSections.length} sections complete
            </p>
          </div>
        </div>
        <div className="partner-form-progress-segments" aria-hidden="true">
          {progressSections.map((section) => (
            <div
              key={section.key}
              className={`partner-form-progress-segment ${section.complete ? 'is-complete' : ''} ${section.percent > 0 && !section.complete ? 'is-active' : ''}`}
            >
              <div className="partner-form-progress-segment-track">
                <span style={{ width: `${section.percent}%` }} />
              </div>
              <span className="partner-form-progress-segment-label">
                {section.label}
                {section.optional ? '' : null}
              </span>
            </div>
          ))}
        </div>
      </div>

      <FormSection
        step="1"
        title={hidePartnerFields ? 'Customer & delivery lead' : 'Partner & customer'}
      >
        <div className="partner-form-grid">
          {!hidePartnerFields ? (
            <>
              <FormField
                label="Partner name"
                required
                hint={isPrefilledPartnerName ? 'From Helix login' : undefined}
                error={displayErrors.partnerName}
                className="partner-form-grid-span-4"
              >
                <input
                  className={controlClass('form-control', Boolean(displayErrors.partnerName))}
                  value={form.partnerName}
                  onChange={(e) => updateField('partnerName', e.target.value)}
                  readOnly={isPrefilledPartnerName}
                />
              </FormField>
              <FormField
                label="Partner CSN"
                required
                hint={isPrefilledCsn ? 'From Helix login' : undefined}
                error={displayErrors.csn}
                className="partner-form-grid-span-4"
              >
                <input
                  className={controlClass('form-control', Boolean(displayErrors.csn))}
                  value={form.csn}
                  onChange={(e) => updateField('csn', e.target.value)}
                  readOnly={isPrefilledCsn}
                />
              </FormField>
              <FormField
                label="Customer name"
                required
                error={displayErrors.customerName}
                className="partner-form-grid-span-4"
              >
                <input
                  className={controlClass('form-control', Boolean(displayErrors.customerName))}
                  placeholder="End customer"
                  value={form.customerName}
                  onChange={(e) => updateField('customerName', e.target.value)}
                />
              </FormField>
            </>
          ) : (
            <FormField
              label="Customer name"
              required
              error={displayErrors.customerName}
              className="partner-form-grid-span-4"
            >
              <input
                className={controlClass('form-control', Boolean(displayErrors.customerName))}
                placeholder="End customer"
                value={form.customerName}
                onChange={(e) => updateField('customerName', e.target.value)}
              />
            </FormField>
          )}
          <FormField
            label="Partner project lead name"
            required
            error={displayErrors.projectLeadName}
            className="partner-form-grid-span-4"
          >
            <input
              className={controlClass('form-control', Boolean(displayErrors.projectLeadName))}
              placeholder="Delivery lead"
              value={form.projectLeadName}
              onChange={(e) => updateField('projectLeadName', e.target.value)}
            />
          </FormField>
          <FormField
            label="Partner project lead email"
            required
            error={displayErrors.projectLeadEmail}
            className="partner-form-grid-span-4"
          >
            <input
              className={controlClass('form-control', Boolean(displayErrors.projectLeadEmail))}
              placeholder="name@yourcompany.com"
              type="email"
              autoComplete="email"
              value={form.projectLeadEmail}
              onChange={(e) => updateField('projectLeadEmail', e.target.value)}
              onBlur={() => {
                if (form.projectLeadEmail && !isValidProjectLeadEmail(form.projectLeadEmail)) {
                  setErrors((previous) => ({
                    ...previous,
                    projectLeadEmail: 'Enter a valid email with @ and . (e.g. name@company.com)',
                  }));
                }
              }}
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection
        step="2"
        title="Project details"
      >
        <div className="partner-form-grid">
          <FormField
            label="Engagement type"
            required
            error={displayErrors.engagementType}
            className="partner-form-grid-span-4"
          >
            <select
              className={controlClass('form-select', Boolean(displayErrors.engagementType))}
              value={form.engagementType}
              onChange={(e) => updateField('engagementType', e.target.value)}
            >
              <option value="">Choose type</option>
              {formOptions.engagementTypes.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Deployment model"
            required
            error={displayErrors.deploymentModel}
            className="partner-form-grid-span-4"
          >
            <select
              className={controlClass('form-select', Boolean(displayErrors.deploymentModel))}
              value={form.deploymentModel}
              onChange={(e) => updateField('deploymentModel', e.target.value)}
            >
              <option value="">Choose model</option>
              {formOptions.deploymentModels.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Project status"
            required
            error={displayErrors.projectStatus}
            className="partner-form-grid-span-4"
          >
            <select
              className={controlClass('form-select', Boolean(displayErrors.projectStatus))}
              value={form.projectStatus}
              onChange={(e) => updateField('projectStatus', e.target.value)}
            >
              <option value="">Choose status</option>
              {projectStatusOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Est. start date"
            required
            error={displayErrors.estimatedStartDate}
            className="partner-form-grid-span-4"
          >
            <PartnerDateInput
              className={controlClass('form-control', Boolean(displayErrors.estimatedStartDate))}
              value={form.estimatedStartDate}
              onChange={(nextValue) => updateField('estimatedStartDate', nextValue)}
            />
          </FormField>
          <FormField
            label="Est. go-live date"
            required
            error={displayErrors.estimatedGoliveDate}
            className="partner-form-grid-span-4"
          >
            <PartnerDateInput
              className={controlClass('form-control', Boolean(displayErrors.estimatedGoliveDate))}
              value={form.estimatedGoliveDate}
              onChange={(nextValue) => updateField('estimatedGoliveDate', nextValue)}
            />
          </FormField>

          <FormField
            label={`Products in scope${selectedProductCount ? ` (${selectedProductCount})` : ''}`}
            required
            error={displayErrors.productsInScope}
            className="partner-form-grid-span-12"
          >
            <div className={`partner-products-grid partner-products-grid--modern ${displayErrors.productsInScope ? 'has-error' : ''}`}>
              {formOptions.productsInScope.map((product) => {
                const isSelected = form.productsInScope.includes(product);
                return (
                  <button
                    key={product}
                    type="button"
                    className={`partner-chip partner-chip--modern ${isSelected ? 'is-selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => onToggleProduct(product)}
                  >
                    <span className="partner-chip-mark" aria-hidden="true">{isSelected ? '✓' : '+'}</span>
                    {product}
                  </button>
                );
              })}
            </div>
          </FormField>

          <FormField
            label="Project description"
            required
            error={displayErrors.projectDescription}
            className="partner-form-grid-span-12"
          >
            <textarea
              className={controlClass('form-control', Boolean(displayErrors.projectDescription))}
              rows={3}
              placeholder="Scope and objectives for PAE review..."
              value={form.projectDescription}
              onChange={(e) => updateField('projectDescription', e.target.value)}
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection
        step="3"
        title="Additional context"
        optional
      >
        <div className="partner-form-grid">
          <FormField
            label="Competitive context"
            className="partner-form-grid-span-4"
          >
            <select
              className="partner-form-select form-select"
              value={form.competitiveContext}
              onChange={(e) => updateField('competitiveContext', e.target.value)}
            >
              <option value="">Select if applicable</option>
              {formOptions.competitiveContexts.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Additional notes for your PAE"
            className="partner-form-grid-span-8"
          >
            <textarea
              className="form-control"
              rows={2}
              placeholder="Optional context for your PAE..."
              value={form.additionalNotes}
              onChange={(e) => updateField('additionalNotes', e.target.value)}
            />
          </FormField>
        </div>
      </FormSection>

      <div className={`partner-form-submit-bar partner-form-submit-bar--modern partner-form-submit-bar--${statusTone}`}>
        <div className="partner-form-submit-status">
          <span className="partner-form-status-icon" aria-hidden="true" />
          <span className={`partner-form-complete-indicator ${statusTone === 'error' ? 'has-error' : ''}`}>
            {statusMessage}
          </span>
        </div>
        <div className="partner-submit-actions">
          <button className="btn btn-primary partner-submit-btn" type="submit" disabled={!validation.isValid || submitting}>
            {submitting ? 'Submitting…' : submitButtonLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ProjectForm;
